// D3 CLIENT / D5-2 — A MEMBER'S DETAIL, AND THE REASON IT IS NOW A MODAL.
//
// THE PATTERN THIS FILE EXISTS TO SET, in one sentence: the answer must land where her thumb is.
//
// D5-2 measured the old panel on her phone. The members table renders every account, so the page was
// 14,693px tall; she tapped "View usage" at y=557, the fetch ran, the handler worked perfectly, and
// the panel rendered at absolute y=14085 — 13,233px below the fold, about fifteen screens down. A
// fixture asserting `detail !== null` passes on that forever. **Wiring was never what was broken.**
// A scrollIntoView patched it; a modal removes the class of bug, because a panel portalled to <body>
// and fixed to the viewport cannot render off-screen no matter how tall the page under it grows.
// Every D3 surface therefore opens through this same door.
//
// EXTRACTED, NOT JUST MOVED. App.tsx was at the R8 ceiling (16230 of 16231) when this order arrived,
// and D2's precedent is that an owner round pays for its lines rather than getting the ceiling
// waived. Props in, tree out, no hook of its own: the two stateful children own their own state.
import type { ReactNode } from "react";
import { OwnerRefunds } from "./OwnerRefunds";
import { OwnerSupportView } from "./OwnerSupportView";
import { OwnerUsagePage } from "./OwnerUsagePage";
import { MemberDeliverySection, type MemberDeliveryPayload } from "./MemberDelivery";
// TS1(4) — the entry type's words come from the law beside the roster, never from this render site
// (R28/R29, and 14-B5's ruling that copy in a TSX file is copy no gate can ask about).
import { serviceEntryLabel } from "../triage.js";

// A type moves with the component that renders it.
export type MemberDetail = {
  member: { id: string; email: string; name: string; joinedAt: string };
  counts: { reports: number; lalaMessages: number; seals: number; journalEntries: number; videosWatched: number; people: number };
  readingsByType: Array<{ type: string; count: number }>;
  usage: Array<{ kind: string; key: string; count: number; lastAt: string }>;
  // DT-4a: her device's chain, which is the form DT-4's verification takes. Optional because a
  // payload written before this shipped has none, and a member modal that throws on an older answer
  // is worse than one that draws no chain (R18: absent is not empty).
  delivery?: MemberDeliveryPayload | null;
  // TS1(4) — her service history. Optional for the same reason `delivery` is: a payload written
  // before this shipped has none, and a modal that throws on an older answer is worse than one that
  // draws no history (R18: absent is not empty).
  service?: Array<{ type: string; actor: string; summary: string; sourceKind: string | null; sourceId: string | null; at: string }> | null;
  privacyNote: string;
};

export function OwnerMemberDetail({ detail, onClose, onDenied, renderReport }: {
  detail: MemberDetail;
  onClose: () => void;
  onDenied: () => void;
  renderReport: (data: unknown) => ReactNode;
}) {
  return (
    <div className="owner-detail">
      <div className="owner-detail__head">
        <h2>{detail.member.name || detail.member.email}</h2>
        <button type="button" className="owner-link" onClick={onClose}>Close</button>
      </div>
      <p className="owner-detail__privacy">{detail.privacyNote}</p>

      {/* D3-1..D3-4 — the support view, behind the second key, ledgered on every open.
          IT IS FIRST IN THE PANEL, AND THAT PLACEMENT IS THE D5-2 LESSON APPLIED ONE LEVEL DOWN.
          Measured at the bottom of this modal on her phone it sat at y=943 on an 852px screen — below
          the fold again, inside the very modal built to stop that. The counts and the payments are
          things she scrolls to; the support door is the thing she came for. */}
      <OwnerSupportView memberId={detail.member.id} onDenied={onDenied} renderReport={renderReport}
        extra={<OwnerUsagePage memberId={detail.member.id} onDenied={onDenied} />} />

      <div className="owner-stats">
        {([["Readings", detail.counts.reports], ["Lala messages", detail.counts.lalaMessages], ["Seals", detail.counts.seals],
           ["Journal entries", detail.counts.journalEntries], ["Videos watched", detail.counts.videosWatched], ["People of interest", detail.counts.people]] as Array<[string, number]>).map(([label, n]) => (
          <div className="owner-stat" key={label}><strong>{n}</strong><span>{label}</span></div>
        ))}
      </div>
      {detail.readingsByType.length ? (
        <>
          <h3 className="owner-detail__sub">Readings by type</h3>
          <ul className="owner-bars">
            {detail.readingsByType.map((r) => (
              <li key={r.type}><span className="owner-bars__label">{r.type}</span><span className="owner-bars__count">{r.count}</span></li>
            ))}
          </ul>
        </>
      ) : null}

      {/* DT-4a — HER DEVICE'S CHAIN. "Sent" is not a success state, and this is where that stops
          being an abstraction about the morning and becomes an answer about one phone. It sits
          inside this payload's own privacy line: a tag, a kind and two timestamps are metadata,
          and there is no field a notification's words could ride in. */}
      <MemberDeliverySection delivery={detail.delivery ?? null} />

      {/* TS1(4) — **EVERYTHING THIS HOUSE HAS DONE FOR THIS MEMBER, IN ONE PLACE, FOREVER.** D4-2's
          one table, append-only by database rule, with three writers and — until her clause 4 — no
          reader at all. **THREE STATES AND NOT TWO** (R18/R64, one panel over): a payload with no
          history field at all is an older answer and draws nothing, an EMPTY history says so in
          words, and rows are rows. Drawing "nothing yet" over an absent field would tell her a member
          has never been served on the strength of a field nobody sent. */}
      {detail.service ? (
        <>
          <h3 className="owner-detail__sub">Service history</h3>
          {detail.service.length ? (
            <ul className="owner-service">
              {detail.service.map((row, i) => (
                <li key={`${row.at}-${i}`}>
                  <span className="owner-service__when">{row.at ? new Date(row.at).toLocaleString() : ""}</span>
                  <strong className={`owner-service__kind is-${row.type}`}>{serviceEntryLabel(row.type)}</strong>
                  <span className="owner-service__what">{row.summary}</span>
                  {/* WHO did it — the house, the agent, or her — because "we told them" and "you told
                      them" are different facts about the same member (R18). */}
                  <em className="owner-service__who">{row.actor}</em>
                </li>
              ))}
            </ul>
          ) : (
            <p className="owner-page__note">Nothing has been recorded for this member yet.</p>
          )}
        </>
      ) : null}

      {/* D2-3 — their real Stripe payments, with a refund per payment. Its own file; the access
          consequence of each refund comes down WITH the payment, out of the shared law in
          src/refunds.js, so this page never decides one. */}
      <OwnerRefunds memberId={detail.member.id} onDenied={onDenied} />
    </div>
  );
}
